import { defineComponent as _defineComponent } from 'vue'
import FormContainer from "../../shared/FormContainer.vue"
import { PropType, ref, provide, readonly, computed, onMounted, watch } from "vue"
import { Theme } from "@/types"
import { THEMES } from "@/constants"
import ServiceList from "@/components/client/onboarding/ServiceList.vue"
import { useQuery } from "@vue/apollo-composable"
import {
  PublicScopeServicesQueryQuery,
  PublicScopeServicesQuery,
  ScopeServiceFragmentFragment,
  MissionRequestInput,
  MissionLocationEnum,
  PromoCode,
  PartnerSource,
  PromoCodeInput as PromoCodeInputType,
} from "@/gql"
import Dropzone from "@/components/widgets/input/Dropzone.vue"
import useRouter from "@/router/useRouter"
import { useNotification } from "@/hooks/useNotification"
import { useOnboardingContext } from "../../shared/useOnboardingContext"
import PromoCodeInput from "@/components/widgets/input/PromoCodeInput/PromoCodeInput.vue"
import Icon from "@/components/widgets/icons/Icon.vue"
import { promoCodePlansMapping } from "@/components/widgets/input/PromoCodeInput/constant"

enum PAGE_STEPS {
  PROJECT_DETAILS_1 = "PROJECT_DETAILS_1",
  PROJECT_DETAILS_2 = "PROJECT_DETAILS_2",
}

type ProjectForm = MissionRequestInput & {
  service: ScopeServiceFragmentFragment | null
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ProjectDetailsForm',
  props: {
  isSubmitting: {
    type: Boolean,
    default: false,
  },
  submitButtonText: {
    type: String,
    default: "Next",
  },
  theme: {
    type: Object as PropType<Theme>,
    default: () => ({
      primaryColor: THEMES.PERALTA_PRIMARY,
      iconColor: THEMES.PERALTA_PRIMARY_RED,
    }),
    required: false,
  },
  activeService: {
    type: Object as PropType<ScopeServiceFragmentFragment | null>,
    required: false,
  },
},
  emits: ["gotoNext", "goto-prev", "change", "partnerCodeChange"],
  setup(__props, { emit }) {

const props = __props


const { navigateTo, routes } = useRouter()


const currentPage = ref(PAGE_STEPS.PROJECT_DETAILS_1)

const subtitle = computed(() => {
  return currentPage.value === PAGE_STEPS.PROJECT_DETAILS_1
    ? "Please tell us about the project you need support with"
    : "Provide us with more details about your project"
})

const iconMapping = {
  1: {
    icon: "pie-chart",
    backgroundColor: "#FF000814",
    iconColor: "#FF0008",
  },
  2: {
    icon: "presentation-graph",
    backgroundColor: "#FFBF2E14",
    iconColor: "#FFBF2E",
  },
  26: {
    icon: "graph-up",
    backgroundColor: "#06A86E14",
    iconColor: "#06A86E",
  },
}
provide("iconMapping", readonly(iconMapping))

const {
  loading,
  result,
  error: publicScopeServicesError,
} = useQuery<PublicScopeServicesQueryQuery>(PublicScopeServicesQuery)

const observer = ref(null)

const form = ref<ProjectForm>({
  projectBrief: "",
  projectLocationType: MissionLocationEnum.RemoteAndOnground,
  termsOfReferenceAttributes: null,
  scopeServiceId: "",
  service: null,
})

const promoCodeInput = ref<PromoCodeInputType>({
  partnerSource: null,
  code: "",
})

watch(
  promoCodeInput,
  (newVal) => {
    emit("partnerCodeChange", newVal)
  },
  { deep: true }
)

const updateActiveService = (service: ScopeServiceFragmentFragment) => {
  form.value.service = service
  form.value.scopeServiceId = service.prn
}

const { addError } = useNotification()

const navigateToHomePage = () => {
  navigateTo({ name: routes.Home })
}

const gotoNext = async () => {
  const validity = await (observer.value as any)?.validateWithInfo()

  if (!validity.isValid) return

  //Check if the user has selected a service
  if (!form.value.scopeServiceId) {
    emit("change", form.value)
    emit("gotoNext")
    return
  }

  currentPage.value = PAGE_STEPS.PROJECT_DETAILS_2
}

const { updateOnboardingForm } = useOnboardingContext()

const onSubmit = async () => {
  if (!form.value.projectBrief && !form.value.termsOfReferenceAttributes) {
    return addError("Please provide a project brief or upload a document")
  }

  updateOnboardingForm({
    missionRequestInput: form.value,
  })

  emit("change", form.value)
  emit("gotoNext")
}

const serviceList = ref<InstanceType<typeof ServiceList> | null>(null)

const serviceListGotoNext = () => {
  if (promoCodeInput.value.code && !promoCodeInput.value.partnerSource) {
    return addError("Please select a partner source")
  }

  serviceList.value?.gotoNext()
}
const partnerCode = ref<PromoCode | null>(null)

const onPartnerCodeComplete = (code: PromoCode) => {
  partnerCode.value = code
  promoCodeInput.value.code = code.code
}

const partnerSourceOptions = [
  {
    text: "I am a portfolio company of the partner",
    value: PartnerSource.PortfolioCompanyOfPartner,
  },
  {
    text: "I am an employee of the partner",
    value: PartnerSource.EmployeeOfPartner,
  },
  {
    text: "I am a contractor working with the partner",
    value: PartnerSource.ContractorOfPartner,
  },
  {
    text: "Other",
    value: PartnerSource.Other,
  },
]
onMounted(() => {
  if (props.activeService) {
    form.value.service = props.activeService
  }
})

return { __sfc: true,PAGE_STEPS, props, navigateTo, routes, emit, currentPage, subtitle, iconMapping, loading, result, publicScopeServicesError, observer, form, promoCodeInput, updateActiveService, addError, navigateToHomePage, gotoNext, updateOnboardingForm, onSubmit, serviceList, serviceListGotoNext, partnerCode, onPartnerCodeComplete, partnerSourceOptions, FormContainer, ServiceList, Dropzone, PromoCodeInput, Icon, promoCodePlansMapping }
}

})